import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { isToday } from 'date-fns';
import { formatDate } from '@sg/garnish';

import { ignoreTimezone } from '@order/utils';

import { type WantedTime } from '../../../machines/ordering-machine.types';
import { adjustWantedTime } from '../../../utils';
import { BagHeaderLink } from '../BagHeaderLink';

/**
 * Time selector for the bag header.
 */
export const BagTimeHeader = (props: BagTimeHeaderProps) => {
  const { time, requestTimeChange } = props;

  const dateInfo = useDateInfo(time);
  const { formatMessage } = useIntl();

  return (
    <BagHeaderLink
      accessibilityLabel={formatMessage(messages.accessibilityLabel)}
      onPress={requestTimeChange}
    >
      <FormattedMessage
        {...messages.time}
        values={{
          state: dateInfo.state,
          day: dateInfo.day,
          time: dateInfo.time,
        }}
      />
    </BagHeaderLink>
  );
};

export const BagTimeConnector = (props: Pick<BagTimeHeaderProps, 'time'>) => {
  const { time } = props;

  const dateInfo = useDateInfo(time);

  return (
    <FormattedMessage
      {...messages.timePrefix}
      values={{ state: dateInfo.state }}
    />
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const useDateInfo = (time?: WantedTime) => {
  const adjustedTime = adjustWantedTime(time);
  const validTime = adjustedTime ? ignoreTimezone(adjustedTime) : null;
  const { formatMessage } = useIntl();

  if (!validTime) {
    return {
      time: formatMessage({
        defaultMessage: 'soon',
        description: 'Bag | Header | Soon',
      }),
      day: '',
      state: 'invalid' as const,
    };
  }

  if (isToday(validTime)) {
    return {
      time: formatDate(validTime, 'h:mma'),
      day: '',
      state: 'today' as const,
    };
  }

  return {
    time: formatDate(validTime, 'h:mma'),
    day: formatDate(validTime, 'EEE'),
    state: 'future' as const,
  };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  timePrefix: {
    defaultMessage: `{state, select,
    today {at}
    future {on}
    other {}
  }`,
    description: 'Bag | Header | Time prefix',
  },
  time: {
    defaultMessage: `{state, select,
    future {{day}, {time}}
    other {{time}}
  }`,
    description: 'Bag | Header | Time prefix',
  },
  accessibilityLabel: {
    defaultMessage: 'Change order time',
    description: 'Bag | Header | Time a11y',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagTimeHeaderProps = {
  time?: WantedTime;
  requestTimeChange: () => void;
};
